import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { ManagerFeedbackActions } from './manager-feedback.actions';
import {
  selectFeedbackList,
  selectPagination,
  selectFilters,
  selectSelectedFeedback,
  selectSelectedFeedbackNotes,
  selectLoading,
  selectDetailLoading,
  selectIsAnyLoading,
  selectError
} from './manager-feedback.selectors';
import {
  FeedbackListParams,
  FeedbackStatus
} from '@app/core/api/manager-feedback';

/**
 * Facade service for manager feedback state management
 * Provides clean API for components to interact with the manager feedback NgRx store
 */
@Injectable({
  providedIn: 'root'
})
export class ManagerFeedbackFacade {
  private readonly store = inject(Store);

  // Observables — List
  readonly list$ = this.store.select(selectFeedbackList);
  readonly pagination$ = this.store.select(selectPagination);
  readonly filters$ = this.store.select(selectFilters);

  // Observables — Detail
  readonly selectedFeedback$ = this.store.select(selectSelectedFeedback);
  readonly selectedFeedbackNotes$ = this.store.select(selectSelectedFeedbackNotes);

  // Observables — Loading
  readonly loading$ = this.store.select(selectLoading);
  readonly detailLoading$ = this.store.select(selectDetailLoading);
  readonly isAnyLoading$ = this.store.select(selectIsAnyLoading);

  // Observables — Error
  readonly error$ = this.store.select(selectError);

  // Actions — List
  loadList(params?: FeedbackListParams): void {
    this.store.dispatch(ManagerFeedbackActions.loadFeedbackList({ params }));
  }

  // Actions — Detail
  loadDetail(id: string): void {
    this.store.dispatch(ManagerFeedbackActions.loadFeedbackDetail({ id }));
  }

  // Actions — Update status
  updateStatus(id: string, status: FeedbackStatus): void {
    this.store.dispatch(ManagerFeedbackActions.updateStatus({ id, status }));
  }

  // Actions — Add note
  addNote(id: string, text: string): void {
    this.store.dispatch(ManagerFeedbackActions.addNote({ id, text }));
  }

  // Actions — Send email
  sendEmail(id: string, subject: string, body: string): void {
    this.store.dispatch(ManagerFeedbackActions.sendEmail({ id, subject, body }));
  }

  // Actions — Delete feedback
  deleteFeedback(id: string): void {
    this.store.dispatch(ManagerFeedbackActions.deleteFeedback({ id }));
  }

  // Actions — Delete note
  deleteNote(feedbackId: string, noteId: string): void {
    this.store.dispatch(ManagerFeedbackActions.deleteNote({ feedbackId, noteId }));
  }

  // Actions — Filters
  setFilters(filters: FeedbackListParams): void {
    this.store.dispatch(ManagerFeedbackActions.setFilters({ filters }));
  }

  // Actions — Clear
  clearSelectedFeedback(): void {
    this.store.dispatch(ManagerFeedbackActions.clearSelectedFeedback());
  }

  clearErrors(): void {
    this.store.dispatch(ManagerFeedbackActions.clearErrors());
  }
}
